/**
 * 404 Not Found Page
 * WCAG 2.1 AAA compliant with proper contrast, semantic structure, and recovery guidance.
 */

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { FileQuestion, Home, ArrowLeft, CheckCircle2 } from "lucide-react";

export function NotFoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-lg">
        <Card className="border-muted rounded-none sm:rounded-lg border-x-0 sm:border-x">
          <CardHeader className="text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted"
              aria-hidden="true"
            >
              <FileQuestion className="h-8 w-8 text-muted-foreground" />
            </div>
            <CardTitle as="h1" className="text-2xl">
              {t("notFound.title")}
            </CardTitle>
            <CardDescription>
              {t("notFound.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Error code for reference */}
            <div className="rounded-lg bg-muted p-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t("notFound.errorCode")}
              </p>
              <p className="font-mono text-lg font-semibold mt-1">404</p>
            </div>

            {/* Recovery suggestions for AAA compliance */}
            <div
              className="rounded-lg border border-muted p-4"
              role="region"
              aria-labelledby="recovery-title"
            >
              <h2 id="recovery-title" className="text-sm font-medium mb-3">
                {t("notFound.suggestions.title")}
              </h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2
                    className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>{t("notFound.suggestions.checkUrl")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2
                    className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>{t("notFound.suggestions.useLink")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2
                    className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>{t("notFound.suggestions.contactAdmin")}</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(-1)}
                aria-label={t("notFound.goBack")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                {t("notFound.goBack")}
              </Button>
              <Button
                className="flex-1"
                onClick={() => navigate("/onboarding")}
                aria-label={t("notFound.goToOnboarding")}
              >
                <Home className="h-4 w-4 mr-2" aria-hidden="true" />
                {t("notFound.goToOnboarding")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
